const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
/**
 * Readable model label: `claude-opus-5-5[1m]` → `{ name: 'Opus 5.5', context: '1M' }`.
 * Unknown ids come back whole (never truncated) so the user can still see what runs.
 */
export function modelParts(id) {
    if (!id)
        return undefined;
    const m1 = /(?:\[1m\]|-1m)$/i.test(id);
    const base = id.replace(/(?:\[1m\]|-1m)$/i, '');
    const c = /^claude-(opus|sonnet|haiku|fable)-(\d+)(?:-(\d{1,2}))?(?:-\d{8})?$/.exec(base);
    if (c) {
        return {
            name: `${cap(c[1])} ${c[2]}${c[3] ? `.${c[3]}` : ''}`,
            ...(m1 ? { context: '1M' } : {}),
        };
    }
    const g = /^gpt-([\d.]+)(?:-([\w-]+))?$/.exec(base);
    if (g) {
        return {
            name: `GPT-${g[1]}${g[2] ? ` ${g[2].split('-').map(cap).join(' ')}` : ''}`,
            ...(m1 ? { context: '1M' } : {}),
        };
    }
    return { name: id };
}
/** `Opus 5.5 · 1M`. */
export function modelLabel(id) {
    const p = modelParts(id);
    return p ? (p.context ? `${p.name} · ${p.context}` : p.name) : undefined;
}
/** Current Claude models (aliases the CLI's `--model` / `set_model` accept). */
export const CLAUDE_MODELS = [
    { id: 'claude-opus-5-5', label: 'Opus 5.5', hint: 'Most capable' },
    { id: 'claude-opus-5-5[1m]', label: 'Opus 5.5 · 1M', hint: '1M-token context' },
    { id: 'claude-sonnet-5-5', label: 'Sonnet 5.5', hint: 'Fast and balanced' },
    { id: 'claude-sonnet-5-5[1m]', label: 'Sonnet 5.5 · 1M', hint: '1M-token context' },
    { id: 'claude-fable-5-1', label: 'Fable 5.1' },
    { id: 'claude-haiku-4-5', label: 'Haiku 4.5', hint: 'Quickest' },
];
const CLAUDE_EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'];
const CODEX_EFFORTS = ['low', 'medium', 'high'];
/**
 * Models offered for an agent. Codex does not expose a model list to the daemon, so it offers
 * the current model only (plus free text in the picker).
 */
export function modelsFor(agent, current) {
    const list = agent === 'claude' ? [...CLAUDE_MODELS] : [];
    if (current && !list.some((m) => m.id === current)) {
        list.unshift({ id: current, label: modelLabel(current) ?? current, hint: 'Current' });
    }
    return list;
}
/** Efforts the agent accepts at session start. */
export function effortsFor(agent) {
    return agent === 'claude' ? CLAUDE_EFFORTS : CODEX_EFFORTS;
}
/** Effort can change mid-run for Codex only; Claude fixes `--effort` at launch. */
export const canSwitchEffort = (agent) => agent === 'codex';
/** `{family, major, minor}` of a Claude model id (`claude-opus-5-5`, `claude-haiku-4-5-2025…`). */
function generation(id) {
    const base = (id ?? '').replace(/(?:\[1m\]|-1m)$/i, '');
    const m = /^claude-(?:opus|sonnet|haiku|fable)-(\d+)(?:-(\d{1,2}))?(?:-\d{8})?$/.exec(base);
    return m ? { major: Number(m[1]), minor: Number(m[2] ?? 0) } : undefined;
}
/**
 * The picker's list. With the CLI's reported `models`, those are the choices (its "Default
 * (recommended)" first); without, the built-in list. Older generations (a lower major version
 * than the newest offered) fold behind "Show older" — except the run's own model, always shown.
 */
export function pickerModels(agent, currentModel, cli) {
    if (agent !== 'claude') {
        const main = modelsFor(agent, currentModel);
        return { main, older: [], current: currentModel };
    }
    const resolved = (o) => o.resolved ?? o.id;
    const list = cli?.length
        ? cli.map((m) => ({
            id: m.value,
            label: m.displayName || modelLabel(m.resolvedModel ?? m.value) || m.value,
            ...(m.description ? { hint: m.description } : {}),
            ...(m.resolvedModel ? { resolved: m.resolvedModel } : {}),
        }))
        : CLAUDE_MODELS.map((m) => ({ ...m }));
    const strip = (s) => (s ?? '').replace(/-\d{8}$/, '');
    const isDefault = (o) => o.id === 'default';
    // The run's model: an exact id, else the entry it resolves to (the default alias last).
    const match = list.find((o) => !isDefault(o) && (o.id === currentModel || strip(resolved(o)) === strip(currentModel))) ?? list.find((o) => strip(resolved(o)) === strip(currentModel));
    if (currentModel && !match)
        list.unshift({
            id: currentModel,
            label: modelLabel(currentModel) ?? currentModel,
            hint: 'Current',
        });
    const current = match?.id ?? currentModel;
    const newest = Math.max(0, ...list.map((o) => generation(resolved(o))?.major ?? 0));
    const main = [];
    const older = [];
    const plain = ({ resolved: _r, ...o }) => o;
    for (const o of list) {
        const g = generation(resolved(o));
        const old = !isDefault(o) && !!g && g.major < newest && o.id !== current;
        (old ? older : main).push(plain(o));
    }
    main.sort((a, b) => Number(isDefault(b)) - Number(isDefault(a)));
    return { main, older, current };
}
//# sourceMappingURL=models.js.map